// File de validation (port du Rec13Queue v13 + cartes offre rec-offre
// RecQueue) — demandes ET versions d'offre en attente, tout le contexte
// de décision sur un écran, pensé mobile. Un refus (commentaire
// obligatoire) renvoie la demande au brouillon / l'offre au
// constructeur.
import { useState } from 'react';
import { ENTITIES } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import {
  useDecideRecRequisition, useRecOfferDecide,
  type RecApplicationDTO, type RecQueueItemDTO, type RecQueueOfferItemDTO,
} from '../../api/hooks.js';
import { BarPill, DimMeter, fmt1 } from './V2Ui.js';
import { useRecSt } from './V2Sections.js';
import { RecBand, RecChain, RecEnt, RecSal, recContrat, recFmtDate, recFmtMoney } from './common.js';
import { OfferDiff } from './Offres.js';

function OfferQueueCard({ it, app, stamp, refusing, com, canAct, onValidate, onRefuseOpen, onRefuseConfirm, onRefuseCancel, setCom }: {
  it: RecQueueOfferItemDTO;
  app?: RecApplicationDTO;
  stamp: { id: string; ok: boolean } | null;
  refusing: string | null;
  com: string;
  canAct: boolean;
  onValidate: () => void;
  onRefuseOpen: () => void;
  onRefuseConfirm: () => void;
  onRefuseCancel: () => void;
  setCom: (s: string) => void;
}) {
  const o = it.offer;
  const ent = ENTITIES.find((e) => e.id === it.entityId);
  const st = useRecSt();
  const g = app?.gates;
  return (
    <div className="rec-q">
      {stamp?.id === o.id && (
        <div className={`rec-stamp ${stamp.ok ? 'ok' : 'ko'}`}>{stamp.ok ? '✓ VALIDÉE' : '✕ REFUSÉE'}</div>
      )}
      <div className="rec-qh">
        <span className="rec-qtype off">Offre · version {o.version}</span>
        {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
        {g && <BarPill bar={g.bar} overridden={g.overridden} st={st} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="rec-av" style={{ background: it.candidate.avColor }}>
          {it.candidate.firstName[0]}{it.candidate.lastName[0]}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-.015em' }}>{it.candidate.firstName} {it.candidate.lastName}</div>
          <div style={{ fontSize: 11, color: 'var(--ink3)', fontWeight: 600 }}>{it.roleLabel} · {it.contrat} · début {recFmtDate(o.startDate)}</div>
        </div>
        {it.juryAvg != null && (
          <span className="rec-agg" style={{ marginLeft: 'auto', marginTop: 0, padding: '7px 13px' }}>
            <span className="big" style={{ fontSize: 18 }}>{it.juryAvg}<i>/5</i></span>
          </span>
        )}
      </div>
      <div className="rec-qgrid">
        <div className="rec-qcell"><div className="k">Montant proposé</div><div className="v">
          {o.salaire != null ? <RecSal band={null}>{recFmtMoney(o.salaire)}</RecSal> : <RecSal band={null} />}
        </div></div>
        <div className="rec-qcell"><div className="k">Verdicts jury</div><div className="v" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {it.juryVerdicts.map((v, i) => (
            <span key={i} className={`rec-vd ${v.verdict}`} style={{ marginLeft: 0 }} title={v.author}>
              {v.verdict === 'oui' ? 'OUI' : v.verdict === 'reserve' ? 'RÉS.' : 'NON'}
            </span>
          ))}
          {!it.juryVerdicts.length && '—'}
        </div></div>
      </div>
      {g?.jury && (
        <div style={{ margin: '4px 0 10px' }}><DimMeter s={g.jury} st={st} sm /></div>
      )}
      {g && (
        <div className="rec-qgrid" style={{ marginTop: 0 }}>
          <div className="rec-qcell"><div className="k">Cas pratique</div><div className="v">{fmt1(g.test.score)}/5 {g.test.pass ? '✓' : '✕'}</div></div>
          <div className="rec-qcell"><div className="k">Vérification</div><div className="v">{g.verif.n} réf. · {g.verif.mgr} manager · intégrité {g.verif.integ ? 'OK' : '—'}</div></div>
        </div>
      )}
      {app?.barOverride && <div className="rv2-ovwarn" style={{ marginBottom: 8 }}>⚖️ Dérogation de {app.barOverride.by.split(' ')[0]} — « {app.barOverride.comment} »</div>}
      {it.band && o.salaire != null && <RecBand band={it.band} val={o.salaire} />}
      {it.band && o.salaire != null && (o.salaire < it.band[0] || o.salaire > it.band[1]) && (
        <div className="rec-outband"><span>⚠</span><span><b>Hors fourchette du rôle</b> ({recFmtMoney(it.band[0])} – {recFmtMoney(it.band[1])}).</span></div>
      )}
      {it.prev && (
        <div style={{ marginTop: 8 }}>
          <div className="rec-lbl">Ce qui change vs v{it.prev.version}{it.prev.counterDetails ? ` — contre-proposition : « ${it.prev.counterDetails} »` : ''}</div>
          <OfferDiff a={it.prev} b={o} canSal={o.salaire != null} />
        </div>
      )}
      <RecChain approvals={o.approvals} />
      {!canAct && it.current && (
        <div className="rec-qwait">⏳ Étape « {it.current.roleLabel} » — la décision revient à l’owner.</div>
      )}
      {canAct && (refusing === o.id ? (
        <div className="rec-refuse">
          <textarea rows={2} autoFocus placeholder="Commentaire obligatoire — retour au constructeur"
            value={com} onChange={(e) => setCom(e.target.value)}></textarea>
          <div className="rec-qbtns">
            <button className="rec-no" disabled={!com.trim()} onClick={onRefuseConfirm}>Confirmer le refus</button>
            <button className="rec-ghost center" onClick={onRefuseCancel}>Annuler</button>
          </div>
        </div>
      ) : (
        <div className="rec-qbtns">
          <button className="rec-ok" onClick={onValidate}>Valider l’offre v{o.version}</button>
          <button className="rec-no" onClick={onRefuseOpen}>Refuser</button>
        </div>
      ))}
    </div>
  );
}

export function RecQueue({ items, apps }: { items: RecQueueItemDTO[]; apps?: RecApplicationDTO[] }) {
  const decide = useDecideRecRequisition();
  const decideOffer = useRecOfferDecide();
  const [stamp, setStamp] = useState<{ id: string; ok: boolean } | null>(null);
  const [refusing, setRefusing] = useState<string | null>(null);
  const [com, setCom] = useState('');
  const busy = decide.isPending || decideOffer.isPending;

  const done = (ok: boolean, msg: string) => {
    setTimeout(() => {
      setStamp(null); setRefusing(null); setCom('');
      toast.success(msg);
    }, 900);
  };
  const fail = (err: unknown) => {
    setStamp(null);
    toast.danger((err as { body?: { message?: string } }).body?.message ?? 'Échec de la décision.');
  };

  const actReq = (id: string, ok: boolean, comment?: string) => {
    setStamp({ id, ok });
    decide.mutate({ id, ok, comment }, {
      onSuccess: () => done(ok, ok
        ? 'Demande validée ✓ — l’annonce peut être publiée'
        : 'Demande refusée — retour au brouillon avec votre commentaire'),
      onError: fail,
    });
  };
  const actOffer = (id: string, ok: boolean, comment?: string) => {
    setStamp({ id, ok });
    decideOffer.mutate({ id, ok, ...(comment ? { comment } : {}) }, {
      onSuccess: () => done(ok,
        ok ? 'Offre validée ✓' : 'Offre refusée — retour au constructeur avec votre commentaire'),
      onError: fail,
    });
  };

  if (!items.length) {
    return (
      <div className="r13-queue r13-done">
        <div className="rec-card-soft rec-qdone">
          <span className="big">🎉</span><b>File vide.</b><br />
          Aucune demande ni offre en attente de votre validation.
        </div>
      </div>
    );
  }

  const nReq = items.filter((i) => i.kind === 'requisition').length;
  const nOff = items.length - nReq;

  return (
    <div className="r13-queue">
      <div className="r13-head">
        <span className="n">{items.length}</span>
        <div className="t">
          {items.length > 1 ? 'décisions' : 'décision'} en attente
          <span>tout le contexte sur un écran — validez ou refusez avec commentaire</span>
        </div>
        <div className="r13-chips">
          {nReq > 0 && <span className="r13-chip dem"><i></i>{nReq} demande{nReq > 1 ? 's' : ''}</span>}
          {nOff > 0 && <span className="r13-chip off"><i></i>{nOff} offre{nOff > 1 ? 's' : ''}</span>}
        </div>
      </div>
      <div className="r13-grid">
        {items.map((it) => {
          if (it.kind === 'offer') {
            return (
              <OfferQueueCard
                key={it.offer.id} it={it} app={apps?.find((a) => a.id === it.applicationId)} stamp={stamp} refusing={refusing} com={com}
                canAct={it.canDecide && !busy}
                onValidate={() => actOffer(it.offer.id, true)}
                onRefuseOpen={() => setRefusing(it.offer.id)}
                onRefuseConfirm={() => actOffer(it.offer.id, false, com.trim())}
                onRefuseCancel={() => { setRefusing(null); setCom(''); }}
                setCom={setCom}
              />
            );
          }
          const r = it.requisition;
          const ent = ENTITIES.find((e) => e.id === r.entityId);
          return (
            <div key={r.id} className="rec-q">
              {stamp?.id === r.id && (
                <div className={`rec-stamp ${stamp.ok ? 'ok' : 'ko'}`}>{stamp.ok ? '✓ VALIDÉE' : '✕ REFUSÉE'}</div>
              )}
              <div className="rec-qh">
                <span className="rec-qtype dem">Demande de recrutement</span>
                {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
              </div>
              <div className="rec-qtitle">{r.roleLabel} <span>· {r.seniority}</span></div>
              <div className="rec-qgrid">
                <div className="rec-qcell"><div className="k">Contrat</div><div className="v">{recContrat(r)}</div></div>
                <div className="rec-qcell"><div className="k">Démarrage cible</div><div className="v">{recFmtDate(r.targetStart)}</div></div>
                <div className="rec-qcell"><div className="k">Motif</div><div className="v">{r.motif}{r.replacedName ? ` ${r.replacedName}` : ''}</div></div>
                <div className="rec-qcell"><div className="k">Fourchette rôle</div><div className="v"><RecSal band={r.band} /></div></div>
              </div>
              {r.justification && <p className="rec-qjustif">« {r.justification} » — <b>{r.createdBy}</b></p>}
              {r.outcomes90.length > 0 && (
                <div className="rec-qoutc">
                  <div className="rec-lbl" style={{ margin: '8px 0 6px' }}>Résultats attendus à 90 jours</div>
                  {r.outcomes90.map((x, i) => <div key={i} className="ln"><span className="rec-vchip">{i + 1}</span>{x}</div>)}
                  {r.outcomes12m.length > 0 && <div className="rec-lbl" style={{ margin: '8px 0 6px' }}>À 12 mois</div>}
                  {r.outcomes12m.map((x, i) => <div key={`y${i}`} className="ln"><span className="rec-vchip">{i + 1}</span>{x}</div>)}
                </div>
              )}
              {r.band && <RecBand band={r.band} val={Math.round((r.band[0] + r.band[1]) / 2)} />}
              <RecChain approvals={r.approvals} />
              {!it.canDecide && it.current && (
                <div className="rec-qwait">⏳ Étape « {it.current.roleLabel} » — la décision revient à l’owner.</div>
              )}
              {it.canDecide && (refusing === r.id ? (
                <div className="rec-refuse">
                  <textarea rows={2} autoFocus placeholder="Commentaire obligatoire — retour au brouillon"
                    value={com} onChange={(e) => setCom(e.target.value)}></textarea>
                  <div className="rec-qbtns">
                    <button className="rec-no" disabled={!com.trim() || busy} onClick={() => actReq(r.id, false, com.trim())}>Confirmer le refus</button>
                    <button className="rec-ghost center" onClick={() => { setRefusing(null); setCom(''); }}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div className="rec-qbtns">
                  <button className="rec-ok" disabled={busy} onClick={() => actReq(r.id, true)}>Valider la demande</button>
                  <button className="rec-no" disabled={busy} onClick={() => setRefusing(r.id)}>Refuser</button>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
